(() => {
  if (customElements.get('vcm-parts-hub')) return;

  const keys = [
    'vcm_q',
    'vcm_brand',
    'vcm_type',
    'vcm_sort',
    'vcm_page'
  ];

  const quote = value =>
    '"' + value.replace(/\\/g, '\\\\').replace(/"/g, '\\"') + '"';

  const query = state => {
    const parts = ['tag:"Supplier-VCM"'];

    if (state.vcm_brand) {
      parts.push('vendor:' + quote(state.vcm_brand));
    }

    if (state.vcm_type) {
      parts.push('product_type:' + quote(state.vcm_type));
    }

    const words =
      (state.vcm_q || '')
        .slice(0, 160)
        .match(/[\p{L}\p{N}][\p{L}\p{N}._-]*/gu) || [];

    words.forEach(word => {
      const token = word.replace(/[-.]/g, '\\$&') + '*';

      parts.push(
        '(' +
        [
          'title',
          'body',
          'vendor',
          'product_type',
          'variants.sku',
          'variants.title'
        ]
          .map(field => field + ':' + token)
          .join(' OR ') +
        ')'
      );
    });

    return parts.join(' AND ');
  };

  class VcmPartsHub extends HTMLElement {
    connectedCallback() {
      if (this.started) return;

      this.form = this.querySelector('form');
      if (!this.form) return;

      this.started = true;
      this.results = this.querySelector('[data-results]');
      this.status = this.querySelector('[data-status]');
      this.page = 1;
      this.sequence = 0;
      this.events = new AbortController();

      const opts = { signal: this.events.signal };

      this.form.addEventListener(
        'submit',
        event => {
          event.preventDefault();
          this.load(1);
        },
        opts
      );

      this.form.querySelectorAll('select').forEach(select => {
        select.addEventListener('change', () => this.load(1), opts);
      });

      this.querySelector('[data-reset]').addEventListener(
        'click',
        () => {
          this.form.reset();
          this.load(1);
        },
        opts
      );

      this.results.addEventListener(
        'click',
        event => {
          const link = event.target.closest('[data-vcm-page]');

          if (
            !link ||
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey
          ) {
            return;
          }

          event.preventDefault();
          this.load(Number(link.dataset.vcmPage), true);
        },
        opts
      );

      this.onPop = () => {
        this.restore();
        this.load(this.page, false, false);
      };

      window.addEventListener('popstate', this.onPop, opts);

      this.restore();

      if (
        keys.some(key =>
          new URL(location.href).searchParams.has(key)
        )
      ) {
        this.load(this.page, false, false);
      }
    }

    disconnectedCallback() {
      this.events?.abort();
      this.request?.abort();
      this.started = false;
    }

    restore() {
      const params = new URL(location.href).searchParams;

      ['vcm_q', 'vcm_brand', 'vcm_type', 'vcm_sort'].forEach(key => {
        const input = this.form.elements.namedItem(key);

        input.value =
          params.get(key) ||
          (key === 'vcm_sort' ? 'relevance' : '');

        if (
          input.tagName === 'SELECT' &&
          input.selectedIndex < 0
        ) {
          input.selectedIndex = 0;
        }
      });

      this.page = Math.max(
        1,
        parseInt(params.get('vcm_page'), 10) || 1
      );
    }

    state() {
      return Object.fromEntries(
        new FormData(this.form).entries()
      );
    }

    async load(page, scroll = false, push = true) {
      const state = this.state();

      const filtered = Boolean(
        state.vcm_q.trim() ||
        state.vcm_brand ||
        state.vcm_type
      );

      const endpoint = filtered
        ? this.dataset.searchUrl
        : this.dataset.collectionUrl;

      if (!endpoint) return;

      const url = new URL(endpoint, location.origin);

      url.searchParams.set('view', 'vcm-results');
      url.searchParams.set('page', String(page));

      if (filtered) {
        url.searchParams.set('q', query(state));
        url.searchParams.set('type', 'product');
        url.searchParams.set(
          'options[unavailable_products]',
          'show'
        );
        url.searchParams.set('options[prefix]', 'none');
        url.searchParams.set(
          'sort_by',
          state.vcm_sort || 'relevance'
        );
      } else if (state.vcm_sort !== 'relevance') {
        url.searchParams.set('sort_by', state.vcm_sort);
      }

      const current = new URL(location.href);

      if (current.searchParams.has('preview_theme_id')) {
        url.searchParams.set(
          'preview_theme_id',
          current.searchParams.get('preview_theme_id')
        );
      }

      this.request?.abort();
      this.request = new AbortController();

      const sequence = ++this.sequence;

      const timeout = setTimeout(
        () =>
          this.request?.signal === signal &&
          this.request.abort(),
        20000
      );

      const signal = this.request.signal;

      this.results.setAttribute('aria-busy', 'true');
      this.status.textContent = 'Finding parts…';

      try {
        const response = await fetch(url, {
          signal,
          credentials: 'same-origin'
        });

        if (!response.ok) {
          throw new Error('Request failed');
        }

        const doc = new DOMParser().parseFromString(
          await response.text(),
          'text/html'
        );

        const fragment = doc.querySelector(
          '[data-vcm-fragment]'
        );

        if (!fragment) {
          throw new Error('Results template missing');
        }

        if (
          sequence !== this.sequence ||
          !this.isConnected
        ) {
          return;
        }

        this.results.replaceChildren(fragment);
        this.page = page;

        this.status.textContent =
          fragment.dataset.count + ' products found.';

        if (push) {
          keys.forEach(key => {
            current.searchParams.delete(key);
          });

          Object.entries(state).forEach(([key, value]) => {
            if (
              value &&
              !(key === 'vcm_sort' && value === 'relevance')
            ) {
              current.searchParams.set(key, value);
            }
          });

          if (page > 1) {
            current.searchParams.set(
              'vcm_page',
              String(page)
            );
          }

          current.searchParams.delete('page');

          history.pushState({}, '', current);
        }

        // Keep pagination links on the hub, including
        // open-in-new-tab navigation.
        this.results
          .querySelectorAll('[data-vcm-page]')
          .forEach(link => {
            const target = new URL(location.href);

            keys.forEach(key => {
              target.searchParams.delete(key);
            });

            Object.entries(state).forEach(([key, value]) => {
              if (value) {
                target.searchParams.set(key, value);
              }
            });

            target.searchParams.set(
              'vcm_page',
              link.dataset.vcmPage
            );

            link.href = target.href;
          });

        if (scroll) {
          this.form.scrollIntoView({
            block: 'start',
            behavior: 'auto'
          });
        }
      } catch (error) {
        if (
          sequence !== this.sequence ||
          !this.isConnected
        ) {
          return;
        }

        this.status.textContent =
          'Could not load parts. Your previous results are still shown. Please press Search parts to retry.';
      } finally {
        clearTimeout(timeout);

        if (sequence === this.sequence) {
          this.results.setAttribute('aria-busy', 'false');
        }
      }
    }
  }

  customElements.define('vcm-parts-hub', VcmPartsHub);
})();